import { DEFAULT_X_AXIS_KEY } from "@ternary/api-lib/analytics/constants";
import {
  ChartType,
  CompareDurationType,
  DataSource,
  DurationType,
  MetricAggregate,
  Operator,
  ProviderType,
  TimeGranularity,
  UnitType,
} from "@ternary/api-lib/analytics/enums";
import {
  ReportDataConfig,
  SortRule,
} from "@ternary/api-lib/analytics/ui/types";
import { Prettify, ReverseMap } from "@ternary/api-lib/utils/TypeUtils";
import { isEqual } from "lodash";
import {
  compressToEncodedURIComponent,
  decompressFromEncodedURIComponent,
} from "lz-string";
import { StringParam, useQueryParam } from "use-query-params";

//
// Constants
//

// Chart Type

const CHART_TYPE_MAP = {
  [ChartType.AREA]: "1",
  [ChartType.CLUSTERED_BAR]: "2",
  [ChartType.KPI]: "3",
  [ChartType.LINE]: "4",
  [ChartType.PIE]: "5",
  [ChartType.STACKED_AREA]: "6",
  [ChartType.STACKED_BAR]: "7",
  [ChartType.TABLE]: "8",
} as const;

type ReverseChartTypeMap = Prettify<ReverseMap<typeof CHART_TYPE_MAP>>;

const REVERSE_CHART_TYPE_MAP = Object.fromEntries(
  Object.entries(CHART_TYPE_MAP).map(([key, value]) => [value, key])
) as ReverseChartTypeMap;

// Compare Duration Type

const COMPARE_DURATION_TYPE_MAP = {
  [CompareDurationType.CUSTOM]: "1",
  [CompareDurationType.INVOICE]: "2",
  [CompareDurationType.PREVIOUS_PERIOD]: "3",
} as const;

type ReverseCompareDurationTypeMap = Prettify<
  ReverseMap<typeof COMPARE_DURATION_TYPE_MAP>
>;

const REVERSE_COMPARE_DURATION_TYPE_MAP = Object.fromEntries(
  Object.entries(COMPARE_DURATION_TYPE_MAP).map(([key, value]) => [value, key])
) as ReverseCompareDurationTypeMap;

// Data Source

const DATA_SOURCE_MAP = {
  [DataSource.AWS_COMPUTE_VISIBILITY]: "1",
  [DataSource.AWS_KUBERNETES_NODE_COST]: "2",
  [DataSource.AWS_KUBERNETES_NODE_USAGE]: "3",
  [DataSource.BIGQUERY_USAGE]: "4",
  [DataSource.BILLING]: "5",
  [DataSource.CARBON_FOOTPRINT]: "6",
  [DataSource.CLOUD_SQL_COST]: "7",
  [DataSource.CLOUD_SQL_INSTANCE_USAGE_DAILY]: "8",
  [DataSource.CLOUD_SQL_INSTANCE_USAGE]: "9",
  [DataSource.DETAILED_BILLING]: "10",
  [DataSource.FOCUS_BILLING]: "11",
  [DataSource.GCP_COMPUTE_VISIBILITY]: "12",
  [DataSource.KUBERNETES_CONTAINER_USAGE]: "13",
  [DataSource.KUBERNETES_NODE_USAGE]: "14",
  [DataSource.SNOWFLAKE_WAREHOUSE_USAGE]: "15",
} as const;

type ReverseDataSourceMap = Prettify<ReverseMap<typeof DATA_SOURCE_MAP>>;

const REVERSE_DATA_SOURCE_MAP = Object.fromEntries(
  Object.entries(DATA_SOURCE_MAP).map(([key, value]) => [value, key])
) as ReverseDataSourceMap;

// Duration Type

const DURATION_TYPE_MAP = {
  [DurationType.CUSTOM]: "1",
  [DurationType.INVOICE]: "2",
  [DurationType.LAST_NINETY_DAYS]: "3",
  [DurationType.LAST_SEVEN_DAYS]: "4",
  [DurationType.LAST_THIRTY_DAYS]: "5",
  [DurationType.MONTH_TO_DATE]: "6",
  [DurationType.QUARTER_TO_DATE]: "7",
  [DurationType.YEAR_TO_DATE]: "8",
  [DurationType.LAST_N_DAYS]: "9",
  [DurationType.LAST_N_MONTHS]: "10",
} as const;

type ReverseDurationTypeMap = Prettify<ReverseMap<typeof DURATION_TYPE_MAP>>;

const REVERSE_DURATION_TYPE_MAP = Object.fromEntries(
  Object.entries(DURATION_TYPE_MAP).map(([key, value]) => [value, key])
) as ReverseDurationTypeMap;

// Metric Aggregate

const METRIC_AGGREGATE_MAP = {
  [MetricAggregate.MAX]: "1",
  [MetricAggregate.MEAN]: "2",
  [MetricAggregate.MIN]: "3",
  [MetricAggregate.SUM]: "4",
} as const;

type ReverseMetricAggregateMap = Prettify<
  ReverseMap<typeof METRIC_AGGREGATE_MAP>
>;

const REVERSE_METRIC_AGGREGATE_MAP = Object.fromEntries(
  Object.entries(METRIC_AGGREGATE_MAP).map(([key, value]) => [value, key])
) as ReverseMetricAggregateMap;

// Operator

const OPERATOR_MAP = {
  [Operator.CONTAINS]: "1",
  [Operator.EQUALS]: "2",
  [Operator.GTE]: "3",
  [Operator.LTE]: "4",
  [Operator.NOT_CONTAINS]: "5",
  [Operator.NOT_EQUALS]: "6",
  [Operator.NOT_SET]: "7",
  [Operator.SET]: "8",
} as const;

type ReverseOperatorMap = Prettify<ReverseMap<typeof OPERATOR_MAP>>;

const REVERSE_OPERATOR_MAP = Object.fromEntries(
  Object.entries(OPERATOR_MAP).map(([key, value]) => [value, key])
) as ReverseOperatorMap;

// Provider Type

const PROVIDER_TYPE_MAP = {
  [ProviderType.ALIBABA]: "1",
  [ProviderType.AWS]: "2",
  [ProviderType.AZURE]: "3",
  [ProviderType.FOCUS]: "4",
  [ProviderType.GCP]: "5",
  [ProviderType.KUBERNETES]: "6",
  [ProviderType.MONGO_DB]: "7",
  [ProviderType.OCI]: "8",
  [ProviderType.SNOWFLAKE]: "9",
  [ProviderType.TERNARY]: "10",
} as const;

type ReverseProviderTypeMap = Prettify<ReverseMap<typeof PROVIDER_TYPE_MAP>>;

const REVERSE_PROVIDER_TYPE_MAP = Object.fromEntries(
  Object.entries(PROVIDER_TYPE_MAP).map(([key, value]) => [value, key])
) as ReverseProviderTypeMap;

// Time Granularity

const TIME_GRANULARITY_MAP = {
  [TimeGranularity.DAY]: "1",
  [TimeGranularity.HOUR]: "2",
  [TimeGranularity.MONTH]: "3",
  [TimeGranularity.QUARTER]: "4",
  [TimeGranularity.WEEK]: "5",
} as const;

type ReverseTimeGranularityMap = Prettify<
  ReverseMap<typeof TIME_GRANULARITY_MAP>
>;

const REVERSE_TIME_GRANULARITY_MAP = Object.fromEntries(
  Object.entries(TIME_GRANULARITY_MAP).map(([key, value]) => [value, key])
) as ReverseTimeGranularityMap;

// Unit Type

const UNIT_TYPE_MAP = {
  [UnitType.BINARY_BYTES]: "1",
  [UnitType.BYTES]: "2",
  [UnitType.CORES]: "3",
  [UnitType.CURRENCY]: "4",
  [UnitType.KILOGRAMS]: "5",
  [UnitType.PERCENTAGE]: "6",
  [UnitType.STANDARD]: "7",
} as const;

type ReverseUnitTypeMap = Prettify<ReverseMap<typeof UNIT_TYPE_MAP>>;

const REVERSE_UNIT_TYPE_MAP = Object.fromEntries(
  Object.entries(UNIT_TYPE_MAP).map(([key, value]) => [value, key])
) as ReverseUnitTypeMap;

/**
 * Default configuration used when starting a new report or when decoding fails
 */
const DEFAULT_REPORT_CONFIG: ReportDataConfig = {
  chartType: ChartType.LINE,
  compareDurationType: null,
  compareEndDate: null,
  compareStartDate: null,
  dataSource: DataSource.BILLING,
  dimensions: [],
  durationType: DurationType.LAST_THIRTY_DAYS,
  endDate: null,
  startDate: null,
  excludedCreditTypes: [],
  excludeOther: false,
  filters: [],
  formula: null,
  formulaAlias: null,
  hiddenMeasures: [],
  excludeNegativeNumbers: false,
  isCumulative: false,
  isFormulaHidden: false,
  isMetricHidden: false,
  invoiceMonthStart: null,
  invoiceMonthEnd: null,
  limit: null,
  measures: [],
  metric: null,
  metricAggregate: null,
  metricFilters: [],
  nLookback: null,
  reverse: false,
  sortRule: null,
  timeGranularity: TimeGranularity.DAY,
  xAxisKey: DEFAULT_X_AXIS_KEY,
};

type MiniDimension = [
  string,
  (typeof PROVIDER_TYPE_MAP)[keyof typeof PROVIDER_TYPE_MAP],
  string,
];

type MiniFilter = [
  string,
  (typeof OPERATOR_MAP)[keyof typeof OPERATOR_MAP],
  (typeof PROVIDER_TYPE_MAP)[keyof typeof PROVIDER_TYPE_MAP],
  string,
  string[] | null,
];

type MiniMeasure = [
  string,
  (typeof PROVIDER_TYPE_MAP)[keyof typeof PROVIDER_TYPE_MAP],
  string,
  (typeof UNIT_TYPE_MAP)[keyof typeof UNIT_TYPE_MAP],
];

/**
 * Compact representation of a report configuration for URL encoding
 */
interface CompactReportConfig {
  // Enums
  ct?: string; // chartType
  ds?: string; // dataSource
  dt?: string; // durationType
  cdt?: string; // compareDurationType
  ma?: string; // metricAggregate
  tg?: string; // timeGranularity

  // Dates
  csd?: string; // compareStartDate
  ced?: string; // compareEndDate
  sd?: string; // startDate
  ed?: string; // endDate
  ims?: string; // invoiceMonthStart
  ime?: string; // invoiceMonthEnd

  // Arrays
  dim?: MiniDimension[]; // dimensions
  ect?: string[]; // excludedCreditTypes
  f?: MiniFilter[]; // filters
  hm?: string[]; // hiddenMeasures
  m?: MiniMeasure[]; // measures
  mf?: MiniFilter[]; // metricFilters

  // Booleans (only include when true)
  enn?: 1; // excludeNegativeNumbers
  eo?: 1; // excludeOther
  ic?: 1; // isCumulative
  ifh?: 1; // isFormulaHidden
  imh?: 1; // isMetricHidden
  r?: 1; // reverse

  // Other fields
  l?: number; // limit
  nl?: number; // nLookback
  fm?: string; // formula
  fa?: string; // formulaAlias
  mt?: string; // metric
  xak?: string; // xAxisKey
  sr?: SortRule; // sortRule
}

/**
 * Encodes report config into a compact JSON string
 * Using a static, direct approach for each field
 */
export function encodeReportConfig(config: Partial<ReportDataConfig>): string {
  const compact: CompactReportConfig = {};

  // Enums
  if (
    config.chartType &&
    config.chartType !== DEFAULT_REPORT_CONFIG.chartType
  ) {
    compact.ct = CHART_TYPE_MAP[config.chartType];
  }

  if (
    config.dataSource &&
    config.dataSource !== DEFAULT_REPORT_CONFIG.dataSource
  ) {
    compact.ds = DATA_SOURCE_MAP[config.dataSource];
  }

  if (
    config.durationType &&
    config.durationType !== DEFAULT_REPORT_CONFIG.durationType
  ) {
    compact.dt = DURATION_TYPE_MAP[config.durationType];
  }

  if (
    config.compareDurationType &&
    config.compareDurationType !== DEFAULT_REPORT_CONFIG.compareDurationType
  ) {
    compact.cdt = COMPARE_DURATION_TYPE_MAP[config.compareDurationType];
  }

  if (
    config.metricAggregate &&
    config.metricAggregate !== DEFAULT_REPORT_CONFIG.metricAggregate
  ) {
    compact.ma = METRIC_AGGREGATE_MAP[config.metricAggregate];
  }

  if (
    config.timeGranularity &&
    config.timeGranularity !== DEFAULT_REPORT_CONFIG.timeGranularity
  ) {
    compact.tg = TIME_GRANULARITY_MAP[config.timeGranularity];
  }

  // Date fields
  if (
    config.compareStartDate &&
    config.compareStartDate !== DEFAULT_REPORT_CONFIG.compareStartDate
  ) {
    compact.csd = config.compareStartDate;
  }

  if (
    config.compareEndDate &&
    config.compareEndDate !== DEFAULT_REPORT_CONFIG.compareEndDate
  ) {
    compact.ced = config.compareEndDate;
  }

  if (
    config.startDate &&
    config.startDate !== DEFAULT_REPORT_CONFIG.startDate
  ) {
    compact.sd = config.startDate;
  }

  if (config.endDate && config.endDate !== DEFAULT_REPORT_CONFIG.endDate) {
    compact.ed = config.endDate;
  }

  if (
    config.invoiceMonthStart &&
    config.invoiceMonthStart !== DEFAULT_REPORT_CONFIG.invoiceMonthStart
  ) {
    compact.ims = config.invoiceMonthStart;
  }

  if (
    config.invoiceMonthEnd &&
    config.invoiceMonthEnd !== DEFAULT_REPORT_CONFIG.invoiceMonthEnd
  ) {
    compact.ime = config.invoiceMonthEnd;
  }

  // Encode array fields (only if they have elements)
  if (config.dimensions && config.dimensions.length > 0) {
    compact.dim = config.dimensions.map((dimension) => [
      dimension.displayName,
      PROVIDER_TYPE_MAP[dimension.providerType],
      dimension.schemaName,
    ]);
  }

  if (config.measures && config.measures.length > 0) {
    compact.m = config.measures.map((measure) => [
      measure.displayName,
      PROVIDER_TYPE_MAP[measure.providerType],
      measure.schemaName,
      UNIT_TYPE_MAP[measure.unit],
    ]);
  }

  if (config.hiddenMeasures && config.hiddenMeasures.length > 0) {
    compact.hm = config.hiddenMeasures;
  }

  if (config.filters && config.filters.length > 0) {
    compact.f = config.filters.map((filter) => [
      filter.displayName,
      OPERATOR_MAP[filter.operator],
      PROVIDER_TYPE_MAP[filter.providerType],
      filter.schemaName,
      filter.values,
    ]);
  }

  if (config.metricFilters && config.metricFilters.length > 0) {
    compact.mf = config.metricFilters.map((filter) => [
      filter.displayName,
      OPERATOR_MAP[filter.operator],
      PROVIDER_TYPE_MAP[filter.providerType],
      filter.schemaName,
      filter.values,
    ]);
  }

  if (config.excludedCreditTypes && config.excludedCreditTypes.length > 0) {
    compact.ect = config.excludedCreditTypes;
  }

  // Encode boolean fields (only include true values)
  if (config.excludeNegativeNumbers === true) {
    compact.enn = 1;
  }

  if (config.excludeOther === true) {
    compact.eo = 1;
  }

  if (config.isCumulative === true) {
    compact.ic = 1;
  }

  if (config.isFormulaHidden === true) {
    compact.ifh = 1;
  }

  if (config.isMetricHidden === true) {
    compact.imh = 1;
  }

  if (config.reverse === true) {
    compact.r = 1;
  }

  // Encode other fields
  if (config.limit && config.limit !== DEFAULT_REPORT_CONFIG.limit) {
    compact.l = config.limit;
  }

  if (
    config.nLookback &&
    config.nLookback !== DEFAULT_REPORT_CONFIG.nLookback
  ) {
    compact.nl = config.nLookback;
  }

  if (config.formula && config.formula !== DEFAULT_REPORT_CONFIG.formula) {
    compact.fm = config.formula;
  }

  if (
    config.formulaAlias &&
    config.formulaAlias !== DEFAULT_REPORT_CONFIG.formulaAlias
  ) {
    compact.fa = config.formulaAlias;
  }

  if (config.metric && config.metric !== DEFAULT_REPORT_CONFIG.metric) {
    compact.mt = config.metric;
  }

  if (config.xAxisKey && config.xAxisKey !== DEFAULT_REPORT_CONFIG.xAxisKey) {
    compact.xak = config.xAxisKey;
  }

  if (
    config.sortRule &&
    !isEqual(config.sortRule, DEFAULT_REPORT_CONFIG.sortRule)
  ) {
    compact.sr = config.sortRule;
  }

  // Use lz-string to compress the JSON string
  return compressToEncodedURIComponent(JSON.stringify(compact));
}

/**
 * Decodes a compact JSON string back into a ReportDataConfig object
 * Using a static, direct approach for each field
 */
function decodeReportConfig(encoded: string): ReportDataConfig {
  try {
    // Use lz-string to decompress the encoded string
    const decoded = decompressFromEncodedURIComponent(encoded);

    const compact = JSON.parse(decoded) as CompactReportConfig;

    // Start with default config
    const config = { ...DEFAULT_REPORT_CONFIG };

    // Decode enum values
    if (compact.ct) {
      config.chartType = REVERSE_CHART_TYPE_MAP[compact.ct];
    }

    if (compact.ds) {
      config.dataSource = REVERSE_DATA_SOURCE_MAP[compact.ds];
    }

    if (compact.dt) {
      config.durationType = REVERSE_DURATION_TYPE_MAP[compact.dt];
    }

    config.compareDurationType =
      compact.cdt !== undefined
        ? REVERSE_COMPARE_DURATION_TYPE_MAP[compact.cdt]
        : null;

    config.metricAggregate =
      compact.ma !== undefined
        ? REVERSE_METRIC_AGGREGATE_MAP[compact.ma]
        : null;

    config.timeGranularity =
      compact.tg !== undefined
        ? REVERSE_TIME_GRANULARITY_MAP[compact.tg]
        : TimeGranularity.DAY;

    // Decode date fields - these will all be null if undefined
    config.compareStartDate = compact.csd ?? null;
    config.compareEndDate = compact.ced ?? null;
    config.startDate = compact.sd ?? null;
    config.endDate = compact.ed ?? null;
    config.invoiceMonthStart = compact.ims ?? null;
    config.invoiceMonthEnd = compact.ime ?? null;

    // Decode array fields - these will all be empty arrays if undefined
    config.dimensions = compact.dim
      ? compact.dim.map(([displayName, providerType, schemaName]) => ({
          displayName,
          providerType: REVERSE_PROVIDER_TYPE_MAP[providerType],
          schemaName,
        }))
      : [];

    config.filters = compact.f
      ? compact.f.map(
          ([displayName, operator, providerType, schemaName, values]) => ({
            displayName,
            operator: REVERSE_OPERATOR_MAP[operator],
            providerType: REVERSE_PROVIDER_TYPE_MAP[providerType],
            schemaName,
            values,
          })
        )
      : [];

    config.measures = compact.m
      ? compact.m.map(([displayName, providerType, schemaName, unit]) => ({
          displayName,
          providerType: REVERSE_PROVIDER_TYPE_MAP[providerType],
          schemaName,
          unit: REVERSE_UNIT_TYPE_MAP[unit],
        }))
      : [];

    config.metricFilters = compact.mf
      ? compact.mf.map(
          ([displayName, operator, providerType, schemaName, values]) => ({
            displayName,
            operator: REVERSE_OPERATOR_MAP[operator],
            providerType: REVERSE_PROVIDER_TYPE_MAP[providerType],
            schemaName,
            values,
          })
        )
      : [];

    config.excludedCreditTypes = compact.ect ?? [];
    config.hiddenMeasures = compact.hm ?? [];

    // Decode boolean fields
    config.excludeNegativeNumbers = compact.enn === 1;
    config.excludeOther = compact.eo === 1;
    config.isCumulative = compact.ic === 1;
    config.isFormulaHidden = compact.ifh === 1;
    config.isMetricHidden = compact.imh === 1;
    config.reverse = compact.r === 1;

    // Decode other fields - these will all be null if undefined
    config.limit = compact.l ?? null;
    config.nLookback = compact.nl ?? null;
    config.formula = compact.fm ?? null;
    config.formulaAlias = compact.fa ?? null;
    config.metric = compact.mt ?? null;
    config.xAxisKey = compact.xak ?? DEFAULT_X_AXIS_KEY;
    config.sortRule = compact.sr ?? null;

    return config;
  } catch (error) {
    console.error("Error decoding report config:", error);
    return DEFAULT_REPORT_CONFIG;
  }
}

/**
 * React hook for managing report state in URL parameters
 */
export function useReportUrlState() {
  const [encodedState, setEncodedState] = useQueryParam(
    "report-state",
    StringParam
  );

  const setReportState = (partialConfig: Partial<ReportDataConfig>) => {
    const currentConfig = getReportState();

    // Handle special cases for array fields - if the partial config has an array field,
    // replace it entirely rather than trying to merge arrays
    const arrayFields = [
      "dimensions",
      "measures",
      "hiddenMeasures",
      "filters",
      "metricFilters",
      "excludedCreditTypes",
    ];

    // Create a new config that merges the current config with the partial updates
    const updatedConfig: ReportDataConfig = {
      ...currentConfig,
      ...partialConfig,
    };

    // Specially handle array fields to ensure they are completely replaced
    // rather than attempting to merge them
    arrayFields.forEach((field) => {
      if (field in partialConfig) {
        updatedConfig[field] = partialConfig[field];
      }
    });

    const encoded = encodeReportConfig(updatedConfig);

    setEncodedState(encoded);
  };

  const getReportState = (): ReportDataConfig => {
    if (!encodedState) return DEFAULT_REPORT_CONFIG;

    return decodeReportConfig(encodedState);
  };

  return [getReportState(), setReportState] as const;
}
